import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LogOut, Trash2, Check, X, Clock, Phone, Mail, Activity, BellOff, ShieldCheck, KeyRound, Loader2, Send, Users, Lock, Unlock } from "lucide-react";
import { unwrapAdminPrivateKey, decryptFromClient } from "@/lib/e2eCrypto";

const STATUS_COLOR = {
    pending: "bg-[#E07A5F] text-white",
    confirmed: "bg-[#3D8C95] text-white",
    cancelled: "bg-[#6E6862] text-white",
};
const STATUS_LABEL = { pending: "En attente", confirmed: "Confirmée", cancelled: "Annulée" };

function loadSession() {
    try {
        const raw = localStorage.getItem("admin_session");
        if (!raw) return null;
        const s = JSON.parse(raw);
        if (!s.token || !s.expires || Date.now() >= s.expires) {
            localStorage.removeItem("admin_session");
            return null;
        }
        return s;
    } catch { return null; }
}

export default function Admin() {
    const [session, setSession] = useState(loadSession);
    const [step, setStep] = useState("password"); // password | code
    const [pwd, setPwd] = useState("");
    const [code, setCode] = useState("");
    const [sessionKey, setSessionKey] = useState("");
    const [loading, setLoading] = useState(false);
    const [bookings, setBookings] = useState([]);
    const [openId, setOpenId] = useState(null);
    // E2E unlock state — private key stays in memory only, never persisted.
    const [e2ePrivKey, setE2ePrivKey] = useState(null);
    const [decrypted, setDecrypted] = useState({});  // {bookingId: {phone, notes}}
    const [showUnlock, setShowUnlock] = useState(false);
    const [unlockBusy, setUnlockBusy] = useState(false);
    const [unlockPassphrase, setUnlockPassphrase] = useState("");
    const backupFileRef = useRef(null);

    const logout = useCallback(() => {
        if (session?.token) {
            api.post("/admin/logout", {}, { headers: { "X-Admin-Token": session.token } }).catch(() => {});
        }
        localStorage.removeItem("admin_session");
        setSession(null);
        setBookings([]);
        setStep("password");
        setPwd("");
        setCode("");
    }, [session]);

    const fetchBookings = useCallback(async () => {
        if (!session?.token) return;
        setLoading(true);
        try {
            const res = await api.get("/admin/bookings", { headers: { "X-Admin-Token": session.token } });
            setBookings(res.data);
        } catch (e) {
            if (e?.response?.status === 401) { toast.error("Session expirée"); logout(); }
            else toast.error("Erreur de chargement");
        } finally { setLoading(false); }
    }, [session, logout]);

    useEffect(() => { if (session) fetchBookings(); }, [session, fetchBookings]);

    // Auto-decrypt all e2e_payload rows whenever we have both a private key + fresh bookings
    useEffect(() => {
        if (!e2ePrivKey || bookings.length === 0) return;
        (async () => {
            const out = { ...decrypted };
            for (const b of bookings) {
                if (!b.e2e_payload || out[b.id]) continue;
                try {
                    out[b.id] = await decryptFromClient(b.e2e_payload, e2ePrivKey);
                } catch {
                    out[b.id] = { error: true };
                }
            }
            setDecrypted(out);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [e2ePrivKey, bookings]);

    const unlockE2E = async () => {
        const file = backupFileRef.current?.files?.[0];
        if (!file) return toast.error("Sélectionnez votre fichier de sauvegarde JSON.");
        if (!unlockPassphrase) return toast.error("Entrez votre phrase de passe.");
        setUnlockBusy(true);
        try {
            const text = await file.text();
            const backup = JSON.parse(text);
            const priv = await unwrapAdminPrivateKey(backup, unlockPassphrase);
            setE2ePrivKey(priv);
            setShowUnlock(false);
            setUnlockPassphrase("");
            toast.success("Chiffrement E2E déverrouillé — vos réservations sont maintenant lisibles.");
        } catch (e) {
            toast.error(e?.message || "Impossible de déverrouiller.");
        } finally {
            setUnlockBusy(false);
        }
    };

    // Timer d'expiration automatique
    useEffect(() => {
        if (!session) return;
        const remain = session.expires - Date.now();
        if (remain <= 0) { logout(); return; }
        const t = setTimeout(() => { toast.info("Session expirée après 12h — reconnexion requise"); logout(); }, remain);
        return () => clearTimeout(t);
    }, [session, logout]);

    const submitPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await api.post("/admin/login", { password: pwd });
            // Si le backend renvoie directement un token → 2FA désactivée → session immédiate
            if (res.data.token) {
                const s = { token: res.data.token, expires: Date.now() + (res.data.expires_in * 1000) };
                localStorage.setItem("admin_session", JSON.stringify(s));
                setSession(s);
                setPwd("");
                toast.success("Connecté — session valide 12h");
                return;
            }
            setSessionKey(res.data.session_key);
            setStep("code");
            toast.success("Un code à 6 chiffres a été envoyé à jaytes2003@gmail.com");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Mot de passe incorrect");
        } finally { setLoading(false); }
    };

    const submitCode = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await api.post("/admin/verify", { code }, { headers: { "X-Session-Key": sessionKey } });
            const s = { token: res.data.token, expires: Date.now() + (res.data.expires_in * 1000) };
            localStorage.setItem("admin_session", JSON.stringify(s));
            setSession(s);
            setStep("password");
            toast.success("Connecté — session valide 12h");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Code incorrect");
        } finally { setLoading(false); }
    };

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/admin/bookings/${id}`, { status }, { headers: { "X-Admin-Token": session.token } });
            toast.success(`Statut : ${STATUS_LABEL[status]}`);
            fetchBookings();
        } catch { toast.error("Erreur"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer définitivement cette réservation ?")) return;
        try {
            await api.delete(`/admin/bookings/${id}`, { headers: { "X-Admin-Token": session.token } });
            toast.success("Supprimée");
            fetchBookings();
        } catch { toast.error("Erreur"); }
    };

    // ==== Écran de connexion ====
    if (!session) {
        return (
            <main data-testid="admin-login-page" className="min-h-screen flex items-center justify-center px-6 pt-24">
                <Card className="w-full max-w-md p-8 rounded-3xl border border-[#E8E2D9] shadow-[0_8px_30px_rgba(44,42,41,0.04)]">
                    <div className="text-xs tracking-[0.3em] uppercase text-[#3D8C95] mb-3">Espace admin sécurisé</div>
                    <h1 className="font-display text-3xl text-[#2C2A29] mb-6">
                        {step === "password" ? "Connexion" : "Vérification"}
                    </h1>

                    {step === "password" ? (
                        <form onSubmit={submitPassword} className="space-y-4">
                            <div className="flex items-start gap-3 text-xs text-[#6E6862] bg-[#3D8C95]/5 rounded-xl p-3 border border-[#3D8C95]/20">
                                <ShieldCheck size={16} className="text-[#3D8C95] mt-0.5 flex-shrink-0" />
                                <span>Après votre mot de passe, un code à 6 chiffres sera envoyé à votre courriel pour valider la connexion.</span>
                            </div>
                            <Input data-testid="admin-password-input" type="password" placeholder="Mot de passe"
                                value={pwd} onChange={(e) => setPwd(e.target.value)}
                                className="rounded-xl" required autoFocus />
                            <Button data-testid="admin-login-button" type="submit" disabled={loading}
                                className="w-full rounded-full bg-[#3D8C95] hover:bg-[#2D7780] text-white py-5">
                                {loading ? <Loader2 size={16} className="animate-spin" /> : "Continuer"}
                            </Button>
                        </form>
                    ) : (
                        <form onSubmit={submitCode} className="space-y-4">
                            <div className="flex items-start gap-3 text-xs text-[#2C2A29] bg-[#E07A5F]/8 rounded-xl p-3 border border-[#E07A5F]/30">
                                <KeyRound size={16} className="text-[#E07A5F] mt-0.5 flex-shrink-0" />
                                <span>Un code à 6 chiffres a été envoyé à <b>jaytes2003@gmail.com</b>. Vérifiez aussi votre dossier spam. Valide 5 minutes.</span>
                            </div>
                            <Input data-testid="admin-code-input" type="text" inputMode="numeric" maxLength={6}
                                placeholder="123456"
                                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                className="rounded-xl text-center text-2xl tracking-[0.5em] font-mono" required autoFocus />
                            <Button data-testid="admin-verify-button" type="submit" disabled={loading || code.length !== 6}
                                className="w-full rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white py-5">
                                {loading ? <Loader2 size={16} className="animate-spin" /> : "Valider et me connecter"}
                            </Button>
                            <button type="button" onClick={() => { setStep("password"); setCode(""); setSessionKey(""); }}
                                className="w-full text-xs text-[#6E6862] hover:text-[#3D8C95] pt-2">
                                ← Retour
                            </button>
                        </form>
                    )}
                </Card>
            </main>
        );
    }

    // ==== Dashboard ====
    const remainH = Math.max(0, Math.floor((session.expires - Date.now()) / 3600000));
    return (
        <main data-testid="admin-dashboard" className="min-h-screen px-6 md:px-12 pt-28 pb-20 max-w-7xl mx-auto">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
                <div>
                    <div className="text-xs tracking-[0.3em] uppercase text-[#3D8C95] mb-2">Tableau de bord</div>
                    <h1 className="font-display text-4xl md:text-5xl text-[#2C2A29]">Réservations</h1>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="text-xs text-[#6E6862] hidden sm:block">
                        Session : {remainH}h restantes
                    </div>
                    {e2ePrivKey ? (
                        <Badge data-testid="e2e-unlocked-badge" className="bg-[#3D8C95]/10 text-[#3D8C95] rounded-full border border-[#3D8C95]/30 hover:bg-[#3D8C95]/10">
                            <Unlock size={12} className="mr-1" /> E2E déverrouillé
                        </Badge>
                    ) : (
                        <Button
                            data-testid="e2e-unlock-btn"
                            size="sm"
                            variant="outline"
                            onClick={() => setShowUnlock(true)}
                            className="rounded-full border-[#3D8C95] text-[#3D8C95]"
                        >
                            <Lock size={14} className="mr-1" /> Déverrouiller E2E
                        </Button>
                    )}
                    <Link to="/admin/setup-e2e">
                        <Button size="sm" variant="ghost" data-testid="e2e-setup-link" className="rounded-full text-[#6E6862]">
                            <KeyRound size={14} className="mr-1" /> E2E
                        </Button>
                    </Link>
                    <Button data-testid="admin-logout" variant="outline" onClick={logout}
                        className="rounded-full border-[#2C2A29] text-[#2C2A29]">
                        <LogOut size={16} className="mr-2" /> Déconnexion
                    </Button>
                </div>
            </div>

            {showUnlock && (
                <Card data-testid="e2e-unlock-panel" className="rounded-3xl border-2 border-[#3D8C95] p-6 mb-8 bg-white">
                    <div className="flex items-center gap-2 mb-4">
                        <ShieldCheck size={18} className="text-[#3D8C95]" />
                        <h3 className="font-display text-xl text-[#2C2A29]">Déverrouiller le chiffrement E2E</h3>
                    </div>
                    <p className="text-sm text-[#6E6862] mb-4 leading-relaxed">
                        Chargez votre fichier de sauvegarde JSON et entrez votre phrase de passe.
                        La clé privée reste uniquement dans votre navigateur — jamais envoyée nulle part.
                    </p>
                    <div className="space-y-3">
                        <input
                            ref={backupFileRef}
                            type="file"
                            accept="application/json,.json"
                            data-testid="e2e-backup-file"
                            className="block w-full text-sm text-[#3A3735] file:mr-4 file:rounded-full file:border-0 file:bg-[#3D8C95] file:text-white file:px-4 file:py-2 file:text-xs"
                        />
                        <Input
                            data-testid="e2e-unlock-passphrase"
                            type="password"
                            value={unlockPassphrase}
                            onChange={(e) => setUnlockPassphrase(e.target.value)}
                            placeholder="Phrase de passe"
                            autoComplete="current-password"
                            className="rounded-full"
                        />
                        <div className="flex gap-2">
                            <Button
                                onClick={unlockE2E}
                                disabled={unlockBusy}
                                data-testid="e2e-do-unlock"
                                className="rounded-full bg-[#3D8C95] hover:bg-[#2D7780] text-white"
                            >
                                {unlockBusy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Unlock size={14} className="mr-2" />}
                                Déverrouiller
                            </Button>
                            <Button
                                variant="ghost"
                                onClick={() => { setShowUnlock(false); setUnlockPassphrase(""); }}
                                className="rounded-full"
                            >
                                Annuler
                            </Button>
                        </div>
                    </div>
                </Card>
            )}

            {loading && <p className="text-[#6E6862]">Chargement...</p>}

            <NewsletterBroadcast token={session.token} />

            <h2 className="font-display text-2xl md:text-3xl text-[#2C2A29] mt-14 mb-5">Réservations récentes</h2>
            {!loading && bookings.length === 0 && (
                <Card className="p-10 rounded-3xl text-center text-[#6E6862]" data-testid="admin-empty">
                    Aucune réservation pour le moment.
                </Card>
            )}

            <div className="space-y-4">
                {bookings.map((b) => {
                    const d = decrypted[b.id];
                    const displayPhone = d && !d.error ? d.phone : b.phone;
                    const displayNotes = d && !d.error ? d.notes : b.notes;
                    return (
                    <Card key={b.id} data-testid={`booking-row-${b.id}`}
                        className="rounded-3xl border border-[#E8E2D9] p-5 md:p-6 bg-white">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                            <div className="flex-1 min-w-[240px]">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <h3 className="font-display text-2xl text-[#2C2A29]">{b.full_name}</h3>
                                    <Badge className={`${STATUS_COLOR[b.status]} rounded-full border-0`}>
                                        {STATUS_LABEL[b.status]}
                                    </Badge>
                                    {b.e2e_payload && (
                                        <Badge
                                            data-testid={`e2e-badge-${b.id}`}
                                            className={`rounded-full border ${
                                                d && !d.error
                                                    ? "bg-[#3D8C95]/10 text-[#3D8C95] border-[#3D8C95]/30"
                                                    : "bg-[#E07A5F]/10 text-[#E07A5F] border-[#E07A5F]/30"
                                            } hover:bg-transparent`}
                                        >
                                            {d && !d.error ? <><Unlock size={11} className="mr-1" />E2E lu</> : <><Lock size={11} className="mr-1" />E2E chiffré</>}
                                        </Badge>
                                    )}
                                    {b.notifications_opt_in === false && (
                                        <Badge data-testid={`notif-off-${b.id}`}
                                            className="bg-[#C44569]/10 text-[#C44569] rounded-full border border-[#C44569]/30 hover:bg-[#C44569]/10">
                                            <BellOff size={12} className="mr-1" /> Notifs OFF
                                        </Badge>
                                    )}
                                </div>
                                <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-[#6E6862]">
                                    <div className="flex items-center gap-2"><Clock size={14} /> {b.date} · {b.time}</div>
                                    <div className="flex items-center gap-2"><Activity size={14} className="text-[#E07A5F]" /> {b.service}</div>
                                    <a href={`tel:${displayPhone}`} className="flex items-center gap-2 hover:text-[#3D8C95]"><Phone size={14} /> {displayPhone}</a>
                                    <a href={`mailto:${b.email}`} className="flex items-center gap-2 hover:text-[#3D8C95] break-all"><Mail size={14} /> {b.email}</a>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {b.status !== "confirmed" && (
                                    <Button data-testid={`confirm-${b.id}`} size="sm" onClick={() => setStatus(b.id, "confirmed")}
                                        className="rounded-full bg-[#3D8C95] hover:bg-[#2D7780] text-white">
                                        <Check size={14} className="mr-1" /> Confirmer
                                    </Button>
                                )}
                                {b.status !== "cancelled" && (
                                    <Button data-testid={`cancel-${b.id}`} size="sm" variant="outline"
                                        onClick={() => setStatus(b.id, "cancelled")}
                                        className="rounded-full border-[#C44569] text-[#C44569]">
                                        <X size={14} className="mr-1" /> Annuler
                                    </Button>
                                )}
                                <Button data-testid={`delete-${b.id}`} size="sm" variant="ghost" onClick={() => remove(b.id)}
                                    className="rounded-full text-[#6E6862] hover:text-[#C44569]">
                                    <Trash2 size={14} />
                                </Button>
                                <Button data-testid={`details-${b.id}`} size="sm" variant="ghost"
                                    onClick={() => setOpenId(openId === b.id ? null : b.id)}
                                    className="rounded-full text-[#3D8C95]">
                                    {openId === b.id ? "Masquer" : "Détails"}
                                </Button>
                            </div>
                        </div>

                        {openId === b.id && (
                            <div className="mt-5 pt-5 border-t border-[#E8E2D9] grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                                <div className="sm:col-span-2">
                                    <Detail label="Notes du client" value={displayNotes} />
                                </div>
                                <div className="text-xs text-[#6E6862]">
                                    Contre-indications : {b.health_consent ? "✅ Attesté" : "❌ Non"}
                                </div>
                                <div className="text-xs text-[#6E6862]">
                                    Billet médical : {b.medical_note_consent ? "✅ Accepté" : "❌ Non"}
                                </div>
                            </div>
                        )}
                    </Card>
                    );
                })}
            </div>
        </main>
    );
}

function Detail({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#6E6862]">{label}</div>
            <div className="text-sm text-[#2C2A29]">{value || "—"}</div>
        </div>
    );
}

function NewsletterBroadcast({ token }) {
    const [count, setCount] = useState(0);
    const [subject, setSubject] = useState("");
    const [html, setHtml] = useState("");
    const [sending, setSending] = useState(false);

    useEffect(() => {
        api.get("/admin/newsletter/subscribers", { headers: { "X-Admin-Token": token } })
            .then((r) => setCount(r.data.count || 0))
            .catch(() => {});
    }, [token]);

    const send = async () => {
        if (!subject.trim() || !html.trim()) return toast.error("Sujet et contenu requis");
        if (!window.confirm(`Envoyer ce courriel à ${count} abonnés ?`)) return;
        setSending(true);
        try {
            const r = await api.post(
                "/admin/newsletter/broadcast",
                { subject, html },
                { headers: { "X-Admin-Token": token } }
            );
            toast.success(`Envoyé à ${r.data.sent} abonnés (${r.data.failed} échecs)`);
            setSubject("");
            setHtml("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur d'envoi");
        } finally {
            setSending(false);
        }
    };

    const loadTemplate = () => {
        setSubject("Offre de printemps · Massoclic");
        setHtml(
`<div style="font-family:Arial,sans-serif;max-width:640px;margin:0 auto;color:#2C2A29;">
  <div style="background:#3D8C95;color:#fff;padding:24px;border-radius:12px 12px 0 0;">
    <h1 style="margin:0;font-size:22px;">Un printemps tout en douceur</h1>
  </div>
  <div style="background:#FAF7F2;padding:24px;border:1px solid #E8E2D9;border-top:none;border-radius:0 0 12px 12px;font-size:14px;line-height:1.6;">
    <p>Bonjour,</p>
    <p>Le printemps arrive et le corps a besoin de se remettre en mouvement. Ce mois-ci, profitez de <b>15 % de rabais sur les forfaits de 6 massages ou plus</b>.</p>
    <p><a href="https://massoclic.ca/#forfaits" style="display:inline-block;background:#E07A5F;color:#fff;padding:12px 24px;border-radius:999px;text-decoration:none;">Voir les forfaits</a></p>
    <p style="color:#6E6862;font-size:12px;">Merci de votre confiance,<br>Jérémy</p>
  </div>
</div>`
        );
    };

    return (
        <Card data-testid="newsletter-broadcast-card" className="rounded-3xl border border-[#E8E2D9] p-6 md:p-8 bg-gradient-to-br from-[#3D8C95]/5 to-[#E07A5F]/5">
            <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
                <div>
                    <div className="text-xs tracking-[0.3em] uppercase text-[#3D8C95] mb-2">
                        Marketing courriel
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl text-[#2C2A29]">Infolettre</h2>
                </div>
                <Badge className="bg-white border border-[#E8E2D9] text-[#2C2A29] rounded-full px-3 py-1" data-testid="subscriber-count">
                    <Users size={14} className="mr-1.5" /> {count} abonnés
                </Badge>
            </div>

            <div className="space-y-3">
                <Input
                    data-testid="newsletter-subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Sujet du courriel"
                    className="rounded-xl bg-white"
                />
                <Textarea
                    data-testid="newsletter-html"
                    value={html}
                    onChange={(e) => setHtml(e.target.value)}
                    placeholder="Contenu HTML de l'infolettre. Vous pouvez utiliser des balises HTML (paragraphe, liens, images)."
                    className="rounded-xl bg-white min-h-[180px] font-mono text-sm"
                />
                <div className="flex flex-wrap gap-2">
                    <Button
                        data-testid="load-template"
                        variant="outline"
                        onClick={loadTemplate}
                        className="rounded-full"
                    >
                        Charger un modèle
                    </Button>
                    <Button
                        data-testid="send-newsletter"
                        onClick={send}
                        disabled={sending || count === 0}
                        className="rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white"
                    >
                        {sending ? (
                            <><Loader2 size={14} className="mr-2 animate-spin" /> Envoi...</>
                        ) : (
                            <><Send size={14} className="mr-2" /> Envoyer à {count} abonnés</>
                        )}
                    </Button>
                </div>
            </div>
        </Card>
    );
}
